import { Component, ErrorInfo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Props { children: ReactNode; }

interface State { hasError: boolean; error: Error | null; lastErrorMessage: string | null; }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, lastErrorMessage: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, lastErrorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    this.setState({ lastErrorMessage: error.message });

    try {
      void supabase
        .from('client_error_log')
        .insert({
          route: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          message: error.message,
          stack: error.stack ?? null,
          component_stack: errorInfo.componentStack ?? null,
        })
        .then(({ error: telemetryError }) => {
          if (telemetryError) {
            console.error('ErrorBoundary telemetry failed:', telemetryError);
          }
        }, (telemetryError) => {
          console.error('ErrorBoundary telemetry failed:', telemetryError);
        });
    } catch (telemetryError) {
      console.error('ErrorBoundary telemetry failed:', telemetryError);
    }
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.lastErrorMessage ?? this.state.error?.message ?? 'Unknown error';
      const stackPreview = this.state.error?.stack?.split('\n').slice(0, 8).join('\n') ?? 'No stack trace available.';

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="text-center max-w-md space-y-4">
            <div className="w-16 h-16 rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center mx-auto">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-all"
            >
              Refresh Page
            </button>
            <details className="text-left rounded-md border border-border bg-card p-4 text-sm text-card-foreground">
              <summary className="cursor-pointer font-medium text-foreground">Technical details</summary>
              <div className="mt-3 space-y-3">
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Error:</strong> {errorMessage}
                </p>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs text-muted-foreground">
                  {stackPreview}
                </pre>
              </div>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
